// composables/useEsunMB.ts
import { ref } from 'vue';
import { esunMB } from 'esunmobilebridge';
import type { CheckAppEmbedStatusResult, CheckCertResult, SignDataResult, APPResult, DeviceType } from 'esunmobilebridge';

// 模組層級單例（所有呼叫者共享）
const isAppEmbedded = ref(false);
const device = ref<DeviceType | null>(null);
const isInitialized = ref(false);
const isDev: boolean = import.meta.env.VITE_IsDevApp === 'true' || false; // 從環境變數讀取是否為開發模式

/**
 * 模組載入時立即同步初始化
 */
function _initEager(): void {
    const status: CheckAppEmbedStatusResult = esunMB.CheckAppEmbedStatus();
    if (isDev) {
        isAppEmbedded.value = true;
        device.value = 999;
        esunMB.InitSDK(status.device);
        isInitialized.value = true;
    } else if (status.code === 0 && (status.device === 0 || status.device === 1)) {
        isAppEmbedded.value = true;
        device.value = status.device;
        esunMB.InitSDK(status.device);
        isInitialized.value = true;
    }
}
_initEager();

export function useEsunMB() {
    /**
     * 檢測 App 內嵌狀態（同步）
     */
    function checkAppEmbedStatus(): CheckAppEmbedStatusResult {
        const status: CheckAppEmbedStatusResult = esunMB.CheckAppEmbedStatus();
        if (isDev) {
            isAppEmbedded.value = true;
            device.value = 999;
            return status;
        }

        if (status.code === 0 && (status.device === 0 || status.device === 1)) {
            isAppEmbedded.value = true;
            device.value = status.device;
        }
        return status;
    }

    /**
     * 初始化 SDK（同步）
     */
    function initSDK(deviceType: DeviceType): void {
        esunMB.InitSDK(deviceType);
        isInitialized.value = true;
    }

    /**
     * 憑證狀態檢查（非同步）
     */
    async function checkCert(idno: string): Promise<CheckCertResult> {
        return await esunMB.CheckCert(idno);
    }

    /**
     * PKCS#7 數位簽章（非同步）
     */
    async function signDataP7(plaintext: string, idno: string): Promise<SignDataResult> {
        return await esunMB.SignDataP7(plaintext, idno);
    }

    /**
     * 另開視窗（非同步）
     */
    async function openNewWindow(url: string, type: number, data?: object): Promise<APPResult> {
        return await esunMB.OpenNewWindow({ url: url, type: type.toString(), data: data });
    }

    /**
     * 關閉 WebView（非同步）
     */
    async function closeWebView(): Promise<APPResult> {
        return await esunMB.CloseWebView();
    }

    /**
     * 取得 channel（I=iOS, A=Android）
     */
    function getChannel(): string {
        if (device.value === 1) return 'I';
        if (device.value === 0) return 'A';
        if (device.value === 999) return 'TEST';
        return '';
    }

    return {
        isAppEmbedded,
        device,
        isInitialized,
        checkAppEmbedStatus,
        initSDK,
        checkCert,
        signDataP7,
        openNewWindow,
        closeWebView,
        getChannel
    };
}
